import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { Button } from 'antd';
import ContentWMax from '../../Components/PageLayout/ContentWMax';
import PageTitle from '../../Components/PageTitle/PageTitle';
import LoadingOverlay from '../../Components/Loading/LoadingOverlay';
import api from '../../services/api';
import styles from './ShiftsPage.module.scss';

Modal.setAppElement('#root');

const weekdayLabels = {
    1: 'Пн',
    2: 'Вт',
    3: 'Ср',
    4: 'Чт',
    5: 'Пт',
    6: 'Сб',
    7: 'Вс',
};

function getMonday(date) {
    const current = new Date(date);
    const day = current.getDay() || 7;
    current.setHours(0, 0, 0, 0);
    current.setDate(current.getDate() - day + 1);
    return current;
}

function formatDateInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatDateLabel(value) {
    if (!value) return '—';
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) return value;
    return `${day}.${month}.${year}`;
}

export default function ShiftsPage() {
    const [weekStart, setWeekStart] = useState(() => formatDateInput(getMonday(new Date())));
    const [loading, setLoading] = useState(false);
    const [savingUserId, setSavingUserId] = useState(null);
    const [data, setData] = useState({ users: [], activeNow: [], upcomingChanges: [], canEdit: false });
    const [draftTemplates, setDraftTemplates] = useState({});
    const [exceptionModal, setExceptionModal] = useState({ isOpen: false, user: null });
    const [exceptionForm, setExceptionForm] = useState({
        date: '',
        mode: 'off',
        startTime: '09:00',
        endTime: '18:00',
        note: '',
    });

    const fetchOverview = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/api/shifts?weekStart=${weekStart}`);
            setData({
                users: Array.isArray(response.data?.users) ? response.data.users : [],
                activeNow: Array.isArray(response.data?.activeNow) ? response.data.activeNow : [],
                upcomingChanges: Array.isArray(response.data?.upcomingChanges) ? response.data.upcomingChanges : [],
                canEdit: Boolean(response.data?.canEdit),
            });
        } catch (error) {
            console.error('Ошибка загрузки смен:', error);
            setData({ users: [], activeNow: [], upcomingChanges: [], canEdit: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchOverview();
    }, [weekStart]);

    useEffect(() => {
        const nextDrafts = {};
        data.users.forEach((user) => {
            nextDrafts[user.id] = Array.isArray(user.templateDays)
                ? user.templateDays.map((day) => ({ ...day }))
                : [];
        });
        setDraftTemplates(nextDrafts);
    }, [data.users]);

    const weekLabels = useMemo(() => {
        const start = new Date(`${weekStart}T00:00:00`);
        return new Array(7).fill(null).map((_, index) => {
            const current = new Date(start);
            current.setDate(start.getDate() + index);
            return {
                date: formatDateInput(current),
                label: `${weekdayLabels[index + 1]}, ${String(current.getDate()).padStart(2, '0')}.${String(current.getMonth() + 1).padStart(2, '0')}`,
            };
        });
    }, [weekStart]);

    const updateDraftDay = (userId, weekday, patch) => {
        setDraftTemplates((prev) => {
            const days = Array.isArray(prev[userId]) ? prev[userId].map((day) => ({ ...day })) : [];
            const index = days.findIndex((day) => Number(day.weekday) === Number(weekday));
            if (index === -1) {
                return prev;
            }

            days[index] = { ...days[index], ...patch };
            return {
                ...prev,
                [userId]: days,
            };
        });
    };

    const saveTemplate = async (userId) => {
        try {
            setSavingUserId(userId);
            await api.put(`/api/shifts/users/${userId}/template`, {
                days: draftTemplates[userId] || [],
            });
            await fetchOverview();
        } catch (error) {
            console.error('Ошибка сохранения шаблона смен:', error);
        } finally {
            setSavingUserId(null);
        }
    };

    const openExceptionModal = (user) => {
        setExceptionModal({ isOpen: true, user });
        setExceptionForm({
            date: weekStart,
            mode: 'off',
            startTime: '09:00',
            endTime: '18:00',
            note: '',
        });
    };

    const closeExceptionModal = () => {
        setExceptionModal({ isOpen: false, user: null });
    };

    const saveException = async () => {
        if (!exceptionModal.user || !exceptionForm.date) {
            return;
        }

        try {
            await api.put(
                `/api/shifts/users/${exceptionModal.user.id}/exceptions/${exceptionForm.date}`,
                {
                    mode: exceptionForm.mode,
                    startTime: exceptionForm.mode === 'custom' ? exceptionForm.startTime : null,
                    endTime: exceptionForm.mode === 'custom' ? exceptionForm.endTime : null,
                    note: exceptionForm.note,
                }
            );
            closeExceptionModal();
            await fetchOverview();
        } catch (error) {
            console.error('Ошибка сохранения исключения:', error);
        }
    };

    const deleteException = async (userId, date) => {
        try {
            await api.delete(`/api/shifts/users/${userId}/exceptions/${date}`);
            await fetchOverview();
        } catch (error) {
            console.error('Ошибка удаления исключения:', error);
        }
    };

    return (
        <ContentWMax>
            <PageTitle title="Смены" />

            <div className={styles.page}>
                {loading ? <LoadingOverlay text="Загружаем смены..." /> : null}

                <div className={styles.headerRow}>
                    <div className={styles.weekNav}>
                        <Button
                            onClick={() => {
                                const next = new Date(`${weekStart}T00:00:00`);
                                next.setDate(next.getDate() - 7);
                                setWeekStart(formatDateInput(next));
                            }}
                        >
                            Прошлая неделя
                        </Button>
                        <div className={styles.weekTitle}>
                            Неделя {formatDateLabel(data.weekStart || weekStart)} - {formatDateLabel(data.weekEnd || weekStart)}
                        </div>
                        <Button
                            onClick={() => {
                                const next = new Date(`${weekStart}T00:00:00`);
                                next.setDate(next.getDate() + 7);
                                setWeekStart(formatDateInput(next));
                            }}
                        >
                            Следующая неделя
                        </Button>
                    </div>
                    <Button onClick={() => fetchOverview()}>Обновить</Button>
                </div>

                <div className={styles.summaryGrid}>
                    <section className={styles.summaryCard}>
                        <h2>Сейчас на смене</h2>
                        {data.activeNow.length === 0 ? (
                            <p className={styles.emptyText}>Сейчас никто не на смене.</p>
                        ) : (
                            <div className={styles.pillList}>
                                {data.activeNow.map((item) => (
                                    <span key={`${item.userId}-${item.name}`} className={styles.pill}>
                                        {item.name} · до {item.endsAt}
                                    </span>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className={styles.summaryCard}>
                        <h2>Изменения на сегодня и завтра</h2>
                        {data.upcomingChanges.length === 0 ? (
                            <p className={styles.emptyText}>Ближайших исключений нет.</p>
                        ) : (
                            <div className={styles.changeList}>
                                {data.upcomingChanges.map((item) => (
                                    <div key={`${item.userId}-${item.date}`} className={styles.changeItem}>
                                        <strong>{item.name}</strong>
                                        <span>{formatDateLabel(item.date)} · {item.label}</span>
                                        {item.note ? <span>{item.note}</span> : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Сотрудник</th>
                                {weekLabels.map((item) => (
                                    <th key={item.date}>{item.label}</th>
                                ))}
                                {data.canEdit ? <th>Действия</th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {data.users.length === 0 ? (
                                <tr>
                                    <td colSpan={data.canEdit ? 9 : 8} className={styles.emptyCell}>
                                        Нет сотрудников для отображения.
                                    </td>
                                </tr>
                            ) : data.users.map((user) => (
                                <tr key={user.id}>
                                    <td className={styles.userCell}>
                                        <strong>{user.name || '—'}</strong>
                                        <span>{user.isOnShiftNow ? `Сейчас на смене: ${user.currentShiftLabel}` : 'Сейчас вне смены'}</span>
                                    </td>
                                    {weekLabels.map((item, index) => {
                                        const weekDay = Array.isArray(user.week) ? user.week[index] : null;
                                        const draftDay = Array.isArray(draftTemplates[user.id])
                                            ? draftTemplates[user.id].find((day) => Number(day.weekday) === index + 1)
                                            : null;

                                        return (
                                            <td key={`${user.id}-${item.date}`} className={styles.shiftCell}>
                                                <div className={styles.shiftLabel}>{weekDay?.label || 'Нет смены'}</div>
                                                {data.canEdit && draftDay ? (
                                                    <div className={styles.editor}>
                                                        <label className={styles.checkboxRow}>
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(draftDay.enabled)}
                                                                onChange={(event) => updateDraftDay(user.id, draftDay.weekday, { enabled: event.target.checked })}
                                                            />
                                                            <span>Смена</span>
                                                        </label>
                                                        <div className={styles.timeRow}>
                                                            <input
                                                                type="time"
                                                                value={draftDay.startTime || '09:00'}
                                                                disabled={!draftDay.enabled}
                                                                onChange={(event) => updateDraftDay(user.id, draftDay.weekday, { startTime: event.target.value })}
                                                            />
                                                            <input
                                                                type="time"
                                                                value={draftDay.endTime || '18:00'}
                                                                disabled={!draftDay.enabled}
                                                                onChange={(event) => updateDraftDay(user.id, draftDay.weekday, { endTime: event.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </td>
                                        );
                                    })}
                                    {data.canEdit ? (
                                        <td className={styles.actionsCell}>
                                            <Button
                                                type="primary"
                                                loading={savingUserId === user.id}
                                                onClick={() => saveTemplate(user.id)}
                                            >
                                                Сохранить
                                            </Button>
                                            <Button onClick={() => openExceptionModal(user)}>Исключение</Button>
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {data.users.length > 0 ? (
                    <section className={styles.summaryCard}>
                        <h2>Исключения</h2>
                        <div className={styles.exceptionsGrid}>
                            {data.users.map((user) => (
                                <div key={`exceptions-${user.id}`} className={styles.exceptionCard}>
                                    <div className={styles.exceptionHeader}>
                                        <strong>{user.name || '—'}</strong>
                                        {data.canEdit ? (
                                            <button type="button" onClick={() => openExceptionModal(user)}>
                                                Добавить
                                            </button>
                                        ) : null}
                                    </div>
                                    {Array.isArray(user.exceptions) && user.exceptions.length > 0 ? (
                                        user.exceptions.map((exception) => (
                                            <div key={`${user.id}-${exception.date}`} className={styles.exceptionRow}>
                                                <div>
                                                    <strong>{formatDateLabel(exception.date)}</strong>
                                                    <span>
                                                        {exception.mode === 'off'
                                                            ? 'Выходной'
                                                            : `${exception.startTime || '—'} - ${exception.endTime || '—'}`}
                                                    </span>
                                                    {exception.note ? <span>{exception.note}</span> : null}
                                                </div>
                                                {data.canEdit ? (
                                                    <button type="button" onClick={() => deleteException(user.id, exception.date)}>
                                                        Удалить
                                                    </button>
                                                ) : null}
                                            </div>
                                        ))
                                    ) : (
                                        <p className={styles.emptyText}>Исключений пока нет.</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                ) : null}
            </div>

            <Modal
                isOpen={exceptionModal.isOpen}
                onRequestClose={closeExceptionModal}
                className={styles.modalContent}
                overlayClassName="appModalOverlay"
                contentLabel="Исключение смены"
            >
                <h2>Исключение смены</h2>
                <div className={styles.modalField}>
                    <label>Сотрудник</label>
                    <div>{exceptionModal.user?.name || '—'}</div>
                </div>
                <div className={styles.modalField}>
                    <label>Дата</label>
                    <input
                        type="date"
                        value={exceptionForm.date}
                        onChange={(event) => setExceptionForm((prev) => ({ ...prev, date: event.target.value }))}
                    />
                </div>
                <div className={styles.modalField}>
                    <label>Режим</label>
                    <select
                        value={exceptionForm.mode}
                        onChange={(event) => setExceptionForm((prev) => ({ ...prev, mode: event.target.value }))}
                    >
                        <option value="off">Выходной</option>
                        <option value="custom">Кастомное время</option>
                    </select>
                </div>
                {exceptionForm.mode === 'custom' ? (
                    <div className={styles.modalField}>
                        <label>Время</label>
                        <div className={styles.timeRow}>
                            <input
                                type="time"
                                value={exceptionForm.startTime}
                                onChange={(event) => setExceptionForm((prev) => ({ ...prev, startTime: event.target.value }))}
                            />
                            <input
                                type="time"
                                value={exceptionForm.endTime}
                                onChange={(event) => setExceptionForm((prev) => ({ ...prev, endTime: event.target.value }))}
                            />
                        </div>
                    </div>
                ) : null}
                <div className={styles.modalField}>
                    <label>Заметка</label>
                    <textarea
                        rows={4}
                        value={exceptionForm.note}
                        onChange={(event) => setExceptionForm((prev) => ({ ...prev, note: event.target.value }))}
                    />
                </div>
                <div className={styles.modalActions}>
                    <Button type="primary" onClick={saveException}>Сохранить</Button>
                    <Button onClick={closeExceptionModal}>Отмена</Button>
                </div>
            </Modal>
        </ContentWMax>
    );
}
